import { Clock } from './Clock';
import { EventBus } from './EventBus';
import type { GameModule } from './GameModule';

/**
 * Central engine. Owns the canvas, GL context, clock, event bus, and
 * all registered modules. Runs the game loop.
 *
 * This is the only "large" coordinator — but it delegates ALL work
 * to modules. It never contains game logic itself.
 */
export class Engine {
  readonly canvas: HTMLCanvasElement;
  readonly gl: WebGL2RenderingContext;
  readonly clock = new Clock();
  readonly events = new EventBus();

  private _modules: GameModule[] = [];
  private _running = false;
  private _rafId = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: true,
      stencil: false,
      premultipliedAlpha: false,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
    });

    if (!gl) {
      throw new Error('WebGL 2.0 is not supported on this device.');
    }

    this.gl = gl;
    this._handleResize();
    window.addEventListener('resize', this._handleResize);
    document.addEventListener('visibilitychange', this._onVisibility);
  }

  /** Register a module. Call before start(). */
  register(module: GameModule): void {
    module.init(this);
    this._modules.push(module);
  }

  /** Start the game loop. */
  start(): void {
    if (this._running) return;
    this._running = true;
    this._rafId = requestAnimationFrame(this._loop);
  }

  /** Stop the game loop. */
  stop(): void {
    this._running = false;
    cancelAnimationFrame(this._rafId);
  }

  /** Stop and dispose all modules. */
  dispose(): void {
    this.stop();
    for (const mod of this._modules) {
      mod.dispose();
    }
    this._modules.length = 0;
    this.events.clear();
    window.removeEventListener('resize', this._handleResize);
    document.removeEventListener('visibilitychange', this._onVisibility);
  }

  private _loop = (now: number): void => {
    if (!this._running) return;

    const dt = this.clock.tick(now);

    for (let i = 0; i < this._modules.length; i++) {
      this._modules[i].update(dt);
    }

    this._rafId = requestAnimationFrame(this._loop);
  };

  private _onVisibility = (): void => {
    if (document.hidden) {
      this.clock.notifyHidden();
    } else {
      const gap = this.clock.notifyVisible(performance.now());
      if (gap > 0.1) this.events.emit('engine:catch-up', { seconds: gap });
    }
  };

  private _handleResize = (): void => {
    const dpr = Math.min(window.devicePixelRatio, 2);
    const w = this.canvas.clientWidth * dpr;
    const h = this.canvas.clientHeight * dpr;

    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
      this.gl.viewport(0, 0, w, h);
      this.events.emit('engine:resize', { width: w, height: h, dpr });
    }
  };
}
